import { canStart } from './canStart';
import { notify } from '../../shared';
import ContactModal from './ContactModal';
import { download } from '../../utilities';
import { formatDate } from '../../utilities';
import { useActions } from '../../molecules';
import { exportContactsCsv } from '../../api';
import { useContacts } from '../../templates';
import { ContactInfo } from '../../templates';
import { CampaignName } from '../../templates';
import { tryParseJSON } from '../../utilities';
import { ContactState } from '../../templates';
import { getErrorMessage } from '../../utilities';
import { ContactsListTemplate } from '../../templates';
import { useIsHovered, useRequest } from '../../hooks';
import { useContactActions } from './useContactActions';
import { Buttons, PaginatePanel } from '../../components';
import { ExternalConversationLink } from '../../templates';
import { useTableSelection, useAppContext } from '../../molecules';
import { addContact, updateContact, deleteContact } from '../../api';
import type { ContactItemType } from '../../templates';

const attentionClass = 'sp-error-outline';
const disabledStartTooltip = 'Wrong contact state or campaign inactive';

const getStateDetails = (item: ContactItemType) => {
  const result = tryParseJSON(item.latest_contact_state_details || '', true);
  return typeof result === 'object' ? result || {} : { details: result };
};

const Contacts = () => {
  const { meta } = useAppContext();

  const listResult = useContacts();
  const { reloading, refresh } = listResult;

  const {
    selected,
    selectedNodes,
    selectAllElement,

    setSelected,
    renderSelectElement,
  } = useTableSelection({ nodes: listResult.nodes });
  const idsForStart = selectedNodes
    .filter(node => canStart(node, meta))
    .map(node => node.id);

  const {
    element,
    isOpened,
    updating,
    initialValues,

    handleClose,
    handleStartAdd,
    handleAdd,
    handleEdit,
    handleStartEdit,
    handleStartDelete,
  } = useActions<ContactItemType, ContactItemType, ContactItemType>({
    name: 'Contact',
    nameKey: 'email',

    onAdd: addContact,
    onEdit: updateContact,
    onDelete: deleteContact,
    onRefresh: refresh,
    deleteConfig: {
      getText: item => {
        return `The user ${item.email} and all related data will be deleted.\nAre you sure?`;
      },
    },
  });

  const {
    progress,

    startElement,
    openStart,

    wipeElement,
    openWipe,

    bulkStartElement,
    openBulkStart,

    stateDetailsElement,
    handleView,

    bulkDeleteElement,
    openBulkDelete,
  } = useContactActions(refresh, setSelected);

  const [handleExport, exporting] = useRequest<any>(
    () => exportContactsCsv(listResult.filters || {}),
    {
      onSuccess: async response => {
        const blob = new Blob([response], { type: 'text/csv;charset=utf-8' });
        download(blob);
      },
      onError: (err: any) =>
        notify.error(getErrorMessage(err, 'Export failed.')),
      deps: [listResult.filters],
    }
  );

  const [isExportHovered, exportHoverProps] = useIsHovered();

  const executing = progress || updating;
  const exportDisabled = !listResult.filters?.campaignId;
  const showAttention = exportDisabled && isExportHovered;

  return (
    <>
      {element}
      {wipeElement}
      {startElement}
      {bulkStartElement}
      {stateDetailsElement}
      {bulkDeleteElement}
      {isOpened && (
        <ContactModal
          loading={executing}
          initialValues={initialValues}
          onClose={handleClose}
          onSumbit={initialValues ? handleEdit : handleAdd}
        />
      )}

      <ContactsListTemplate
        listResult={listResult}
        filtersClassNames={{ campaignId: showAttention ? attentionClass : '' }}
        buttons={[
          {
            icon: 'refresh',
            loading: reloading,
            onClick: refresh,
            label: 'Refresh',
          },
          {
            icon: 'plus',
            type: 'primary',
            label: 'Add Contact',
            action: 'manage_outreach_contacts',
            onClick: () => handleStartAdd(),
          },

          {
            icon: 'delete',
            type: 'danger',
            action: 'manage_outreach_contacts',
            loading: executing,
            disabled: !selected.length,
            onClick: () => openBulkDelete(selected),
            label: `Delete (${selected.length})`,
          },
          {
            icon: 'play',
            action: 'manage_outreach_contacts',
            loading: executing,
            onClick: () => openBulkStart(idsForStart),
            disabled: executing || !idsForStart.length,
            label: `Start for (${idsForStart.length})`,
          },
          {
            ...exportHoverProps,
            loading: exporting,
            disabled: exportDisabled,
            onClick: handleExport,
            tooltip: exportDisabled ? 'Select a Campaign' : undefined,
            label: 'Export CSV',
          },
        ]}
        columns={[
          {
            header: selectAllElement,
            getValue: contact => renderSelectElement(contact),
          },
          {
            header: 'Contact',
            sortName: 'email',
            cellClassName: 'text-small',
            getValue: contact => (
              <ContactInfo
                contact={contact}
                withMessagesLink
                suffix={
                  <ExternalConversationLink
                    asBlock={false}
                    contactPhone={contact.phone}
                  />
                }
              />
            ),
          },
          {
            header: 'Campaign',
            getValue: ({ campaign_id: id }) => {
              const campaign = meta.campaigns.find(v => v.value === id);
              const name = campaign?.label || id || '';

              return (
                <CampaignName
                  id={id}
                  name={name}
                  withId={false}
                  withMonitoringLink
                  withMonitoringLinkIcon={false}
                />
              );
            },
          },
          {
            header: 'State',
            sortName: 'latest_contact_state',
            cellClassName: 'max-w-[15rem]',
            getValue: ({
              campaign_id,
              latest_contact_state,
              latest_contact_state_label,
              latest_contact_state_details,
            }) => (
              <ContactState
                className="max-w-[13rem]"
                campaignId={campaign_id}
                latest_contact_state={latest_contact_state}
                latest_contact_state_label={latest_contact_state_label}
                latest_contact_state_details={latest_contact_state_details}
              />
            ),
          },
          {
            header: 'Description',
            sortName: 'description',
            cellClassName: 'text-tiny',
            getValue: ({ description }) => description,
          },
          {
            header: 'Created',
            sortName: 'created_at',
            cellClassName: 'text-tiny',
            getValue: ({ created_at }) => formatDate(created_at),
          },
          {
            header: 'Actions',
            align: 'right',
            getValue: item => {
              const noStart = !canStart(item, meta);

              return (
                <Buttons
                  visibleCount={2}
                  buttons={[
                    {
                      icon: 'question',
                      label: 'State Details',
                      tooltip: 'Last State Details',
                      disabled: !item.latest_contact_state_details,
                      onClick: () => handleView(getStateDetails(item)),
                    },
                    {
                      icon: 'play',
                      label: 'Start',
                      type: 'primary',
                      onClick: () => openStart(item),
                      action: 'manage_outreach_contacts',
                      disabled: noStart,
                      tooltip: !noStart
                        ? 'Start Campaign'
                        : disabledStartTooltip,
                    },
                    {
                      icon: 'edit',
                      label: 'Edit',
                      tooltip: 'Edit Contact',
                      action: 'manage_outreach_contacts',
                      onClick: () => handleStartEdit(item),
                    },

                    {
                      icon: 'eraser',
                      label: 'Clear',
                      action: 'manage_outreach_contacts',
                      tooltip: 'Clear Contact Data',
                      onClick: () => openWipe(item),
                    },
                    {
                      icon: 'delete',
                      type: 'danger',
                      label: 'Delete',
                      action: 'manage_outreach_contacts',
                      tooltip: 'Delete Contact',
                      onClick: () => handleStartDelete(item),
                    },
                  ]}
                />
              );
            },
          },
        ]}
      >
        <PaginatePanel
          {...listResult.pagination}
          setPage={listResult.setPage}
        />
      </ContactsListTemplate>
    </>
  );
};

export default Contacts;
